import React, { createContext, useContext, useMemo, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  API_URL as baseURL,
  AUTO3P_BACKEND_URL as LEGACY_API_URL,
} from 'config'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'

import { sendErrorReport } from '@/utils/helpers/sendErrorReport'
import {
  LOGIN_PATH as LOGIN_PAGE_PATH,
  DOCUMENT_PATH,
  VEHICLE_EVENTS_PATH,
  DRIVER_PATH,
  WORKSHOP_EVENTS_PATH,
  BOOKING_PATH,
  REQUEST_PHOTOS_PATH,
} from '@/utils/routePaths'

import {
  SERVICE_BOOKING_BASE_PATH,
  VEHICLE_EVENTS_BASE_PATH,
} from '@/services/endpoints'
import { REFRESH_TOKEN_PATH, LOGIN_PATH } from '@/services/endpoints'

import {
  AUTHORIZATION_COOKIE_KEY,
  REMEMBER_ME_COOKIE_KEY,
  REMEMBER_ME_COOKIE_EXPIRATION_DAYS,
  SWITCH_SESSION_COOKIE_KEY,
} from '@/blocks/Auth/definitions'
import { useAuthCookieManager } from '@/blocks/Auth/hooks/useAuthCookieManager'
import {
  clearTokenLoginFailed,
  endTokenLogin,
  isTokenLoginFailed,
  markTokenLoginFailed,
  tryBeginTokenLogin,
} from '@/blocks/Auth/tokenLoginGuard'

let serverVersion = ''

export const getServerVersion = () => serverVersion

const ApiContext = createContext(null)

export const ApiProvider = ({ children }) => {
  const api = axios.create()

  const { saveAuthCookie, getAuthTokenFromCookie } = useAuthCookieManager()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const _jwtToken = getAuthTokenFromCookie(AUTHORIZATION_COOKIE_KEY)
  const jwtToken = useRef(_jwtToken)
  jwtToken.current = _jwtToken

  const loginToken =
    getAuthTokenFromCookie(REMEMBER_ME_COOKIE_KEY) ??
    getAuthTokenFromCookie(SWITCH_SESSION_COOKIE_KEY)

  const retryRequestWithToken = (request, token) => {
    request.headers.Authorization = `Bearer ${token}`
    request._retry = true
    return api.request(request)
  }

  const isOnLoginPage = () =>
    location.pathname.startsWith(LOGIN_PAGE_PATH)

  const loginWithRememberMeToken = async () => {
    if (!loginToken || isTokenLoginFailed() || isOnLoginPage()) return null
    if (!tryBeginTokenLogin()) return null

    try {
      const { data } = await api.post(
        LOGIN_PATH,
        { token: loginToken },
        {
          apiType: 'legacy',
          isLoginWithToken: true,
        },
      )

      if (!data?.JWTToken) {
        markTokenLoginFailed()
        return null
      }

      const { JWTToken: newJwtToken, loginToken: newLoginToken } = data

      jwtToken.current = newJwtToken
      saveAuthTokens({ newJwtToken, newLoginToken })
      queryClient.resetQueries()
      clearTokenLoginFailed()

      return newJwtToken
    } catch {
      markTokenLoginFailed()
      return null
    } finally {
      endTokenLogin()
    }
  }

  const isLoggedInWithTokenUrlParam = () => {
    const pagePath = location.pathname.toLocaleLowerCase()
    return (
      pagePath.startsWith(VEHICLE_EVENTS_PATH.toLocaleLowerCase()) ||
      pagePath.startsWith(WORKSHOP_EVENTS_PATH.toLocaleLowerCase()) ||
      pagePath.startsWith(DOCUMENT_PATH.toLocaleLowerCase()) ||
      pagePath.startsWith(BOOKING_PATH.toLocaleLowerCase()) ||
      pagePath.startsWith(DRIVER_PATH.toLocaleLowerCase()) ||
      pagePath.startsWith(REQUEST_PHOTOS_PATH.toLocaleLowerCase())
    )
  }

  const saveAuthTokens = ({ newJwtToken, newLoginToken }) => {
    if (newJwtToken)
      saveAuthCookie({
        cookieName: AUTHORIZATION_COOKIE_KEY,
        token: newJwtToken,
      })

    if (newLoginToken) {
      saveAuthCookie({
        cookieName: newLoginToken
          ? REMEMBER_ME_COOKIE_KEY
          : SWITCH_SESSION_COOKIE_KEY,
        token: newLoginToken,
        temporary: false,
        expiresInDays: REMEMBER_ME_COOKIE_EXPIRATION_DAYS,
      })
    }
  }
  // Add request interceptor to handle baseURL and auth
  api.interceptors.request.use((config) => {
    // apiType can be 'legacy', 'auth', or 'public'
    if (config.apiType === 'auth') {
      config.baseURL = baseURL
    } else {
      config.baseURL = LEGACY_API_URL
    }

    config.headers.lang = document.lang || 'en'

    if (config.apiType === 'public') {
      delete config.headers.Authorization
      return config
    }

    if (jwtToken && jwtToken.current && !config.isLoginWithToken) {
      config.headers.Authorization = `Bearer ${jwtToken.current}`
    }

    return config
  })

  api.interceptors.response.use(
    (response) => {
      const nextServerVersion = response?.headers?.['x-app-version']

      if (nextServerVersion && serverVersion !== nextServerVersion) {
        serverVersion = nextServerVersion
      }

      return response
    },
    async (error) => {
      const originalRequest = error.config

      const lastPage = location.pathname + location.search + location.hash
      const loginWithRedirect = `${LOGIN_PAGE_PATH}?redirect-url=${encodeURIComponent(
        lastPage,
      )}`

      if (error.response?.status === 401) {
        // ignore if the user is logged in with a token url param
        if (isLoggedInWithTokenUrlParam()) return Promise.reject(error)

        if (originalRequest.isLoginWithToken || originalRequest._retry) {
          navigate(loginWithRedirect)
          return Promise.reject(error)
        }

        if (originalRequest.isRefreshToken) {
          const newJwtToken = await loginWithRememberMeToken()

          if (newJwtToken) {
            return retryRequestWithToken(originalRequest, newJwtToken)
          }

          navigate(loginWithRedirect)
          return Promise.reject(error)
        }

        try {
          const { data } = await api.get(REFRESH_TOKEN_PATH, {
            apiType: 'legacy',
            headers: {
              Authorization: `Bearer ${jwtToken.current}`,
            },
            isRefreshToken: true,
          })

          if (data?.token) {
            const newJwtToken = data.token

            jwtToken.current = newJwtToken
            saveAuthTokens({ newJwtToken })
            queryClient.resetQueries()

            return retryRequestWithToken(originalRequest, newJwtToken)
          }
        } catch {
          const newJwtToken = await loginWithRememberMeToken()

          if (newJwtToken) {
            return retryRequestWithToken(originalRequest, newJwtToken)
          }

          navigate(loginWithRedirect)
          return Promise.reject(error)
        }

        navigate(loginWithRedirect)
        return Promise.reject(error)
      } else if (
        error.response?.status === 404 &&
        error.response.config.isLoginWithToken
      ) {
        // If the login token is expired, navigate to the login page
        navigate(loginWithRedirect)
      } else {
        if (!ignoreErrorReport(error, originalRequest)) {
          const message = [
            `Url: /${originalRequest.url}`,
            `Method: ${originalRequest.method}`,
            `Request: ${originalRequest.data}`,
            `Response-data: ${
              error?.response?.data?.length > 0
                ? JSON.stringify(error?.response.data[0])
                : ''
            }`,
            `Response-status: ${error?.response?.status}: ${error?.response?.statusText}`,
          ].join('\n')
          sendErrorReport(error?.code, error?.message, message)
        }
      }

      return Promise.reject(error)
    },
  )

  const ignoreErrorReport = (error, originalRequest) => {
    const status = error?.response?.status ?? 0
    const url = originalRequest?.url ?? ''

    const isBooking = url.includes(SERVICE_BOOKING_BASE_PATH)
    const isVehicleEvents = url.includes(VEHICLE_EVENTS_BASE_PATH)

    return (isBooking || isVehicleEvents) && status >= 400 && status < 500
  }

  const value = useMemo(
    () => ({
      api,
    }),
    [api],
  )

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

ApiProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useLegacyAuthApi = () => {
  const context = useContext(ApiContext)
  if (!context)
    throw new Error('useLegacyAuthApi must be used within ApiProvider')

  return {
    ...context.api,
    request: (config) => context.api.request({ ...config, apiType: 'legacy' }),
    get: (url, config) =>
      context.api.get(url, { ...config, apiType: 'legacy' }),
    post: (url, data, config) =>
      context.api.post(url, data, { ...config, apiType: 'legacy' }),
    delete: (url, config) =>
      context.api.delete(url, { ...config, apiType: 'legacy' }),
    put: (url, data, config) =>
      context.api.put(url, data, { ...config, apiType: 'legacy' }),
  }
}

export const useAuthApi = () => {
  const context = useContext(ApiContext)
  if (!context) throw new Error('useAuthApi must be used within ApiProvider')

  return {
    ...context.api,
    request: (config) => context.api.request({ ...config, apiType: 'auth' }),
    get: (url, config) => context.api.get(url, { ...config, apiType: 'auth' }),
    post: (url, data, config) =>
      context.api.post(url, data, { ...config, apiType: 'auth' }),
    put: (url, data, config) =>
      context.api.put(url, data, { ...config, apiType: 'auth' }),
  }
}

export const useLegacyPublicApi = () => {
  const context = useContext(ApiContext)
  if (!context) throw new Error('usePublicApi must be used within ApiProvider')

  return {
    ...context.api,
    request: (config) => context.api.request({ ...config, apiType: 'public' }),
    get: (url, config) =>
      context.api.get(url, { ...config, apiType: 'public' }),
    post: (url, data, config) =>
      context.api.post(url, data, { ...config, apiType: 'public' }),
    put: (url, data, config) =>
      context.api.put(url, data, { ...config, apiType: 'public' }),
  }
}
